import {
  Alert,
  Button,
  Card,
  Divider,
  Dropdown,
  Flex,
  Form,
  Input,
  Modal,
  Space,
  Table,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { notifyError, notifySuccess } from '../../../store/utils';
import { API } from '../../../api';
import { NetworkResult } from '../../../common/CommonResult';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import ProvinceSelect from '../../../lib/ProvinceSelect';
import DistrictSelect from '../../../lib/DistrictSelect';
import StreetSelect from '../../../lib/StreetSelect';

const AreaAddressList = () => {
  const { areaNo } = useParams();
  const [state, setState] = useState<NetworkResult<any>>({
    loading: true,
    result: {},
  });

  const [visible, setVisible] = useState(false);

  const fetchCourierItem = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(
        `/settings/courier_area/address/${areaNo}`
      );
      setState({
        loading: false,
        result,
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [areaNo]);

  useEffect(() => {
    if (areaNo) {
      fetchCourierItem();
    }
  }, [areaNo]);

  const changeZipcodeArea = useCallback(async (areaItemNo: string, streetNo: number) => {
    try {
      const payload = {
        itemNo: areaItemNo,
        streetNo,
      };
      await API.put({ apiVersion: 'api' })(`/settings/courier_area/address/${areaNo}`, payload);
      notifySuccess('Амжилттай');
      fetchCourierItem();
    } catch (error: any) {
      notifyError(error.message);
    }
  }, []);

  const columns = [
    {
      title: 'Зип код',
      dataIndex: 'zipcode',
    },
    {
      title: 'Аймаг хот',
      key: 'district',
      render: (record: any) => record?.province?.name,
    },
    {
      title: 'Сум дүүрэг',
      key: 'sumDuureg',
      render: (record: any) => record?.district?.name,
    },
    {
      title: 'Баг хороо',
      dataIndex: 'name',
    },
    {
      title: 'Үйлдэл',
      width: 120,
      render: (val: any, row: any) => (
        <Dropdown
          menu={{
            items: (state.result?.data?.branchArea?.areas || []).map((item: any) => {
              if (item.no == areaNo) {
                return {
                  key: item.no,
                  label: item.name,
                  disabled: true,
                };
              } else {
                return {
                  key: item.no,
                  label: <div onClick={() => changeZipcodeArea(item.no, val.no)}>{item.name}</div>,
                };
              }
            }),
          }}
          placement="bottomLeft"
          arrow={{ pointAtCenter: true }}
        >
          <EditOutlined />
        </Dropdown>
      ),
    },
  ];

  return (
    <Card
      title={`Бүсийн хаяг`}
      loading={state.loading}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setVisible(true)}>
          Хаяг нэмэх
        </Button>
      }
    >
      <Alert
        message="Бүсийн мэдээлэл"
        description={`Хүргэлт: ${state?.result?.data?.branchArea?.name} Бүс: ${state?.result?.data?.name}`}
        type="info"
      />
      <Divider />
      <Table
        bordered
        columns={columns}
        size="small"
        pagination={{ pageSize: 50 }}
        dataSource={state?.result?.data?.addresses || []}
      />
      <AddressCreate
        visible={visible}
        onClose={() => setVisible(false)}
        reload={() => fetchCourierItem()}
      />
    </Card>
  );
};

interface AddressCreateProps {
  visible: boolean;
  onClose: () => void;
  reload?: () => void;
}

const AddressCreate = ({ visible, onClose, reload }: AddressCreateProps) => {
  const { areaNo } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = useCallback(
    async (values: any) => {
      try {
        setLoading(true);
        const payload = {
          streetNo: values.streetNo,
        };
        await API.post({ apiVersion: 'api' })(`/settings/courier_area/address/${areaNo}`, payload);
        notifySuccess('Амжилттай хадгаллаа');
        close();
        if (reload) reload();
      } catch (error: any) {
        notifyError(error.message);
      } finally {
        setLoading(false);
      }
    },
    [areaNo]
  );

  const provinceHandler = useCallback(
    (value?: string) => {
      form.setFieldsValue({
        districtNo: undefined,
        streetNo: undefined,
      });
    },
    [form]
  );

  const disrictHandler = useCallback(
    (value?: string) => {
      form.setFieldValue('streetNo', undefined);
    },
    [form]
  );

  const close = useCallback(() => {
    form.resetFields();
    onClose();
  }, []);

  return (
    <Modal title="Хаяг нэмэх" footer={null} open={visible} onCancel={close}>
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Form.Item
          name="provinceNo"
          label="Аймаг хот"
          rules={[{ required: true, message: 'Аймаг хот оруулна уу' }]}
        >
          <ProvinceSelect onChange={(value?: string) => provinceHandler(value)} />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.provinceNo !== currentValues.provinceNo
          }
        >
          {({ getFieldValue }) =>
            getFieldValue('provinceNo') ? (
              <Form.Item name="districtNo">
                <DistrictSelect
                  provinceNo={getFieldValue('provinceNo')}
                  onChange={(value?: string) => disrictHandler(value)}
                  allowClear
                />
              </Form.Item>
            ) : (
              <Form.Item>
                <DistrictSelect allowClear />
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.districtNo !== currentValues.districtNo
          }
        >
          {({ getFieldValue }) =>
            getFieldValue('districtNo') ? (
              <Form.Item name="streetNo">
                <StreetSelect districtNo={getFieldValue('districtNo')} allowClear />
              </Form.Item>
            ) : (
              <Form.Item>
                <StreetSelect />
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item>
          <Flex justify="end" gap={12}>
            <Button onClick={() => close()}>Буцах</Button>
            <Button loading={loading} type="primary" htmlType="submit">
              Хадгалах
            </Button>
          </Flex>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AreaAddressList;
